import md5 from "md5";
import { Timestamp } from "firebase/firestore";
import { useEffect, useState } from "react";
import {
  createPlayer,
  GameDoc,
  GameType,
  generateToken,
  getAllGames,
  getGame,
  getPlayer,
  mergeTokens,
  saveAllGames,
  saveGame,
  updatePlayerStats,
  getPlayerStats,
} from "./playerService";
import {
  GameStateHistory,
  getFinishedGameStatsFromLocalStorage,
  getSettings,
  loadGameStateFromLocalStorageNew,
  saveGameStateToLocalStorage,
  saveSettings,
  updateFinishedGameStats,
  gameStateKeyNew,
  gameStatisticsKey,
} from "./localStorage";
import { PlayContext } from "./playContext";
import {
  saveAllResultsToFirebase,
  saveGameResultFirebase,
} from "./dataAdapter";
import { PlayState } from "./statuses";
import { auth } from "./settingsFirebase";
import { logError, logInfo } from "./logService";
import { onAuthStateChanged } from "firebase/auth";
import { getPlayerByGoogleUid, linkGoogleUid } from "./playerService";
import {
  GAME_TYPE_WORDLE5,
  LEGACY_WRITES_ENABLED,
  incrementGuessDistribution,
  EMPTY_DISTRIBUTION,
} from "./statsCalculation";

const TOKEN_KEY = "playerToken";
const MIGRATION_KEY = "tokenMigrationStatus";

export const getToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY);
};

export const setToken = (token: string): void => {
  localStorage.setItem(TOKEN_KEY, token);
};

const getMigrationStatus = (): string | null => {
  return localStorage.getItem(MIGRATION_KEY);
};

const setMigrationStatus = (status: string): void => {
  localStorage.setItem(MIGRATION_KEY, status);
};

/** Sync settings from Firestore PlayerDoc to localStorage. */
const syncSettingsFromPlayer = (player: import("./playerService").PlayerDoc): void => {
  const current = getSettings(false);
  const updated = {
    ...current,
    darkMode: player.darkMode,
    colorBlindMode: player.colorBlindMode,
    bigFont: player.bigFont,
    nickname: player.nickname || current.nickname,
  };
  saveSettings(updated);
};

/** Wait for Firebase Auth to resolve the persisted session (resolves once). */
const waitForAuthReady = (): Promise<void> =>
  new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, () => {
      unsubscribe();
      resolve();
    });
  });

/**
 * Restore localStorage game data from backup if the main key is empty
 * but a backup exists (written during initial migration attempt).
 */
const restoreFromBackupIfNeeded = (): void => {
  const current = localStorage.getItem(gameStateKeyNew);
  const backup = localStorage.getItem("backup_actualGameState");
  if ((!current || current === "{}") && backup) {
    logInfo("[Migration] Restoring game state from backup");
    localStorage.setItem(gameStateKeyNew, backup);
  }
  const currentStats = localStorage.getItem(gameStatisticsKey);
  const backupStats = localStorage.getItem("backup_stats");
  if (!currentStats && backupStats) {
    logInfo("[Migration] Restoring stats from backup");
    localStorage.setItem(gameStatisticsKey, backupStats);
  }
};

export const initializePlayer = async (
  onMigrating?: (migrating: boolean) => void
): Promise<string> => {
  await waitForAuthReady();
  const googleUser = auth.currentUser;
  const googleUid = googleUser?.uid || null;

  // 1. Check for existing token
  const existingToken = getToken();
  if (existingToken) {
    // Verify player exists in Firestore
    try {
      const player = await getPlayer(existingToken);
      if (player) {
        syncSettingsFromPlayer(player);
        // Link Google UID if signed in but not yet linked
        if (googleUid && !player.googleUid) {
          await linkGoogleUid(existingToken, googleUid);
        }
        // Retry migration if it wasn't completed previously
        if (getMigrationStatus() !== "completed") {
          try {
            restoreFromBackupIfNeeded();
            onMigrating?.(true);
            await migrateLocalStorageToFirestore(existingToken);
            setMigrationStatus("completed");
            onMigrating?.(false);
          } catch (err) {
            logError("Migration retry failed", { error: String(err) });
            onMigrating?.(false);
          }
        }
        await syncFirestoreToLocalStorage(existingToken);
        return existingToken;
      }
    } catch (err) {
      logError("Error verifying player, continuing with token", { error: String(err) });
      return existingToken;
    }
    // Player doesn't exist in Firestore — recreate
    const settings = getSettings(false);
    try {
      await createPlayer(existingToken, {
        nickname: settings.nickname,
        darkMode: settings.darkMode,
        colorBlindMode: settings.colorBlindMode,
        bigFont: settings.bigFont,
      });
      if (googleUid) {
        await linkGoogleUid(existingToken, googleUid);
      }
      // Retry migration after recreating player doc
      if (getMigrationStatus() !== "completed") {
        try {
          restoreFromBackupIfNeeded();
          await migrateLocalStorageToFirestore(existingToken);
          setMigrationStatus("completed");
        } catch (err) {
          logError("Migration retry after recreate failed", { error: String(err) });
        }
      }
    } catch (err) {
      logError("Error recreating player", { error: String(err) });
    }
    return existingToken;
  }

  // 2. If signed into Google, check if a player already exists for this UID
  if (googleUid) {
    try {
      const existing = await getPlayerByGoogleUid(googleUid);
      if (existing) {
        syncSettingsFromPlayer(existing.player);
        setToken(existing.token);
        await syncFirestoreToLocalStorage(existing.token);
        return existing.token;
      }
    } catch (err) {
      logError("Error looking up player by Google UID", { error: String(err) });
    }
  }

  // 3. Check for old userId — migration path
  const settings = getSettings(false);
  const oldUserId = settings.userId;

  // Backup localStorage data before generating new token, in case migration goes wrong
  const existingGameState = localStorage.getItem(gameStateKeyNew);
  const existingStats = localStorage.getItem(gameStatisticsKey);
  if (existingGameState) {
    localStorage.setItem("backup_actualGameState", existingGameState);
  }
  if (existingStats) {
    localStorage.setItem("backup_stats", existingStats);
  }

  const token = generateToken();
  const migrationStart = performance.now();

  try {
    await createPlayer(token, {
      nickname: settings.nickname,
      darkMode: settings.darkMode,
      colorBlindMode: settings.colorBlindMode,
      bigFont: settings.bigFont,
      migratedFromUserId: oldUserId,
    });

    // Link Google UID if already signed in
    if (googleUid) {
      await linkGoogleUid(token, googleUid);
    }

    // Migrate existing localStorage data to Firestore
    if (getMigrationStatus() !== "completed") {
      onMigrating?.(true);
      await migrateLocalStorageToFirestore(token);
      setMigrationStatus("completed");
      onMigrating?.(false);
    }

    const migrationDuration = performance.now() - migrationStart;
    logInfo("[Migration] Player creation & data migration completed", {
      durationMs: Math.round(migrationDuration),
      token,
    });
  } catch (err) {
    const migrationDuration = performance.now() - migrationStart;
    logError("[Migration] Failed", {
      durationMs: Math.round(migrationDuration),
      error: String(err),
    });
    // App still works from localStorage
  }

  setToken(token);
  return token;
};

const migrateLocalStorageToFirestore = async (
  token: string
): Promise<void> => {
  const history = loadGameStateFromLocalStorageNew();
  if (!history) return;

  const entries = Object.entries(history);
  const gameType: GameType = GAME_TYPE_WORDLE5;
  const startTime = performance.now();
  let migratedCount = 0;
  let skippedCount = 0;

  const games: Record<string, GameDoc> = {};

  for (const [key, item] of entries) {
    const match = key.match(/^day(\d+)$/);
    if (!match) {
      skippedCount++;
      continue;
    }

    const solutionIndex = parseInt(match[1], 10);
    if (isNaN(solutionIndex)) {
      skippedCount++;
      continue;
    }

    games[key] = {
      gameType,
      solutionIndex,
      guesses: item.guesses || [],
      isGameWon: item.isGameWon || false,
      isGameLoose: item.isGameLoose || false,
      solutionMd5: item.solutionMd5 || "",
      startTime: item.startTime || null,
      endTime: item.endTime || null,
    };
    migratedCount++;
  }

  try {
    await saveAllGames(token, gameType, games);
  } catch (err) {
    logError("Error migrating games to Firestore", { error: String(err) });
  }

  // Migrate stats
  const localStats = getFinishedGameStatsFromLocalStorage();
  if (localStats && localStats.guessesDistribution) {
    const gamesPlayed = localStats.guessesDistribution.reduce(
      (a, b) => a + b,
      0
    );
    try {
      await updatePlayerStats(token, gameType, {
        guessesDistribution: localStats.guessesDistribution,
        gamesPlayed,
        lastUpdated: Timestamp.now(),
      });
    } catch (err) {
      logError("Error migrating stats", { error: String(err) });
    }
  }

  const duration = performance.now() - startTime;
  logInfo("[Migration] Migration complete", {
    migratedCount,
    skippedCount,
    durationMs: Math.round(duration),
  });
};

export const loadTodaysGame = async (
  token: string,
  playContext: PlayContext
): Promise<{
  guesses: string[];
  isGameWon: boolean;
  isGameLoose: boolean;
  startTime?: number;
  endTime?: number;
} | null> => {
  try {
    const game = await getGame(token, GAME_TYPE_WORDLE5, playContext.solutionIndex);
    if (game) {
      return {
        guesses: game.guesses,
        isGameWon: game.isGameWon,
        isGameLoose: game.isGameLoose,
        startTime: game.startTime || undefined,
        endTime: game.endTime || undefined,
      };
    }
  } catch (err) {
    logError("Error loading game from Firestore", { error: String(err) });
  }
  return null;
};

const saveGameToFirestore = async (
  token: string,
  gameDoc: GameDoc
): Promise<void> => {
  await saveGame(
    token,
    GAME_TYPE_WORDLE5,
    gameDoc.solutionIndex,
    gameDoc
  );
};

const saveLegacyCollections = (
  playContext: PlayContext,
  guesses: string[],
  isGameWon: boolean,
  numberOfGuesses: number,
  duration?: number
): void => {
  if (!LEGACY_WRITES_ENABLED) return;

  const result: PlayState = isGameWon ? "win" : "loose";

  // Legacy: gameResult collection (read by `statistics` cloud function)
  saveGameResultFirebase(
    playContext,
    "TBD",
    result,
    guesses,
    numberOfGuesses,
    duration
  );

  // Legacy: allResults collection (backward compat)
  saveAllResultsToFirebase();
};

const updateAllStats = async (
  token: string,
  isGameWon: boolean,
  numberOfGuesses: number
): Promise<void> => {
  // Update localStorage stats
  updateFinishedGameStats(isGameWon, numberOfGuesses);

  // Update Firestore player stats
  const existing = await getPlayerStats(token, GAME_TYPE_WORDLE5);
  const dist = existing
    ? incrementGuessDistribution(
        existing.guessesDistribution,
        isGameWon,
        numberOfGuesses
      )
    : incrementGuessDistribution(
        [...EMPTY_DISTRIBUTION],
        isGameWon,
        numberOfGuesses
      );
  const gamesPlayed = (existing?.gamesPlayed || 0) + 1;
  await updatePlayerStats(token, GAME_TYPE_WORDLE5, {
    guessesDistribution: dist,
    gamesPlayed,
    lastUpdated: Timestamp.now(),
  });
};

export type SaveGuessResult = {
  success: boolean;
  errors: string[];
};

export const saveGuess = async (
  token: string,
  playContext: PlayContext,
  guesses: string[],
  isGameWon: boolean,
  isGameLoose: boolean,
  startTime?: number,
  endTime?: number
): Promise<SaveGuessResult> => {
  const errors: string[] = [];

  // 1. Save to localStorage (cache)
  saveGameStateToLocalStorage(
    guesses,
    playContext,
    isGameWon,
    isGameLoose,
    startTime,
    isGameWon || isGameLoose ? endTime : undefined
  );

  // 2. On game end, save to Firestore + legacy writes + stats update
  if (isGameWon || isGameLoose) {
    const gameDoc: GameDoc = {
      gameType: GAME_TYPE_WORDLE5,
      solutionIndex: playContext.solutionIndex,
      guesses,
      isGameWon,
      isGameLoose,
      solutionMd5: md5(playContext.solution),
      startTime: startTime || null,
      endTime: endTime || null,
    };

    try {
      await saveGameToFirestore(token, gameDoc);
    } catch (err) {
      logError("Error saving game to Firestore", { error: String(err) });
      errors.push("Firestore game save failed");
    }

    const numberOfGuesses = guesses.length - 1;
    const duration =
      startTime && endTime ? endTime - startTime : undefined;

    saveLegacyCollections(
      playContext,
      guesses,
      isGameWon,
      numberOfGuesses,
      duration
    );

    try {
      await updateAllStats(token, isGameWon, numberOfGuesses);
    } catch (err) {
      logError("Error updating player stats", { error: String(err) });
      errors.push("Stats update failed");
    }
  }

  return { success: errors.length === 0, errors };
};

export const enterExternalToken = async (
  currentToken: string,
  enteredToken: string
): Promise<{ success: boolean; totalGames: number; merged: number; conflicts: number; error?: string }> => {
  // Validate format
  if (!/^[A-Z0-9]{10}$/.test(enteredToken)) {
    return { success: false, totalGames: 0, merged: 0, conflicts: 0, error: "Neplatný formát kódu. Kód musí mít 10 znaků (A-Z, 0-9)." };
  }

  if (enteredToken === currentToken) {
    return { success: false, totalGames: 0, merged: 0, conflicts: 0, error: "Nelze sloučit se stejným kódem." };
  }

  // Verify target token exists and count its games
  let totalGames = 0;
  try {
    const player = await getPlayer(enteredToken);
    if (!player) {
      return { success: false, totalGames: 0, merged: 0, conflicts: 0, error: "Kód nebyl nalezen." };
    }
    const stats = await getPlayerStats(enteredToken, GAME_TYPE_WORDLE5);
    if (stats) {
      totalGames = stats.gamesPlayed;
    }
  } catch (err) {
    return { success: false, totalGames: 0, merged: 0, conflicts: 0, error: "Chyba při ověřování kódu." };
  }

  // Merge current into target (copies any games from current device to target)
  try {
    const result = await mergeTokens(currentToken, enteredToken);
    totalGames += result.merged; // add newly merged games to total
    // Switch to new token and sync Firestore data to localStorage
    setToken(enteredToken);
    await syncFirestoreToLocalStorage(enteredToken);
    return { success: true, totalGames, merged: result.merged, conflicts: result.conflicts };
  } catch (err) {
    return { success: false, totalGames: 0, merged: 0, conflicts: 0, error: "Chyba při slučování dat." };
  }
};

/**
 * Syncs Firestore games and stats into localStorage so it serves
 * as a reliable offline fallback. Merges games (Firestore wins on conflict).
 */
export const syncFirestoreToLocalStorage = async (token: string): Promise<void> => {
  try {
    const firestoreGames = await loadAllGamesFromFirestore(token);
    const localRaw = localStorage.getItem(gameStateKeyNew);
    const localGames = localRaw ? JSON.parse(localRaw) : {};
    const merged = { ...localGames, ...firestoreGames };
    localStorage.setItem(gameStateKeyNew, JSON.stringify(merged));

    const firestoreStats = await getPlayerStats(token, GAME_TYPE_WORDLE5);
    if (firestoreStats) {
      localStorage.setItem(gameStatisticsKey, JSON.stringify({
        guessesDistribution: firestoreStats.guessesDistribution,
      }));
    }
  } catch (err) {
    logError("syncFirestoreToLocalStorage failed, localStorage unchanged", { error: String(err) });
  }
};

/**
 * Loads all games for a player from Firestore and returns them
 * in the GameStateHistory format (compatible with statisticsLib).
 */
export const loadAllGamesFromFirestore = async (
  token: string
): Promise<GameStateHistory> => {
  const allGamesDoc = await getAllGames(token, GAME_TYPE_WORDLE5);
  const firestoreHistory: GameStateHistory = {};
  if (allGamesDoc) {
    for (const [key, game] of Object.entries(allGamesDoc.games)) {
      firestoreHistory[key] = {
        guesses: game.guesses,
        isGameWon: game.isGameWon,
        isGameLoose: game.isGameLoose,
        solutionMd5: game.solutionMd5,
        startTime: game.startTime || undefined,
        endTime: game.endTime || undefined,
      };
    }
  }
  const firestoreKeyCount = Object.keys(firestoreHistory).length;

  // Merge with localStorage (Firestore wins on conflicts)
  const localRaw = localStorage.getItem(gameStateKeyNew);
  const localGames: GameStateHistory = localRaw ? JSON.parse(localRaw) : {};
  const merged = { ...localGames, ...firestoreHistory };

  // If local had extra games, save merged result back to Firestore
  if (Object.keys(merged).length > firestoreKeyCount) {
    try {
      const gameDocs: Record<string, GameDoc> = {};
      for (const [key, game] of Object.entries(merged)) {
        const solutionIndex = parseInt(key.replace("day", ""), 10);
        gameDocs[key] = {
          guesses: game.guesses,
          isGameWon: game.isGameWon ?? false,
          isGameLoose: game.isGameLoose ?? false,
          solutionMd5: game.solutionMd5 ?? "",
          startTime: game.startTime ?? null,
          endTime: game.endTime ?? null,
          gameType: GAME_TYPE_WORDLE5,
          solutionIndex,
        };
      }
      await saveAllGames(token, GAME_TYPE_WORDLE5, gameDocs);
    } catch (err) {
      logError("Failed to sync merged games to Firestore", { error: String(err) });
    }
  }

  return merged;
};

/**
 * Hook that returns game history from database
 */
export const useGameHistory = (
  token: string | null
): { history: GameStateHistory | undefined; loading: boolean } => {
  const [history, setHistory] = useState<GameStateHistory | undefined>(
    undefined
  );
  const [loading, setLoading] = useState(!!token);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    loadAllGamesFromFirestore(token)
      .then((data) => {
        if (!cancelled) {
          setHistory(data);
        }
      })
      .catch((err) => {
        logError("Error loading games from Firestore", { error: String(err) });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return { history, loading };
};

/**
 * Hook that returns personal stats: tries localStorage first,
 * falls back to Firestore player stats.
 */
export const usePersonalStats = (
  token: string | null
): {
  stats: { guessesDistribution: number[] } | undefined;
  loading: boolean;
} => {
  const [stats, setStats] = useState<
    { guessesDistribution: number[] } | undefined
  >(undefined);
  const [loading, setLoading] = useState(!!token);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    getPlayerStats(token, GAME_TYPE_WORDLE5)
      .then((data) => {
        if (!cancelled && data) {
          setStats({
            guessesDistribution: data.guessesDistribution,
          });
        }
      })
      .catch((err) => {
        logError("Error loading stats from Firestore", { error: String(err) });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return { stats, loading };
};
